import { type RefObject, useEffect } from 'react';
import { _notNil } from '@/littledash';

/**
 * Configuration for a single element binding.
 *
 * @template F Name of the property defined on the element.
 * @template E Type of the element the property is defined on.
 * @template R Type the accessor resolves to, and therefore the type of the property.
 */
type UseElementBindingProps<F extends string, E extends HTMLElement, R> = {
  /** Property name to define on the element. */
  field: F;
  /** Ref holding the element to bind to. A null ref is a no-op. */
  element: RefObject<E | null>;
  /** Invoked on each property read to resolve the current value. */
  accessor: () => R;
};

/**
 * Exposes a value on a DOM element as a lazily-evaluated, non-enumerable property,
 * providing a side-channel for out-of-band readers to inspect component internals
 * that are not otherwise reflected in the DOM.
 *
 * The property is defined once the element is available and removed when the binding
 * is torn down. It is non-enumerable so it stays absent from spreads, `Object.keys`
 * and serialization walks of the node, and configurable so it can be redefined.
 *
 * The accessor is captured when the binding is established, so it must read through
 * a ref rather than closing over render values, which would be permanently stale.
 *
 * @example
 * const containerRef = useRef<HTMLDivElement | null>(null);
 * const hotInstanceRef = useRef<Handsontable | null>(null);
 *
 * useElementBinding({
 *   element: containerRef,
 *   field: 'hotInstance',
 *   accessor: useCallback(() => hotInstanceRef.current, []),
 * });
 */
const useElementBinding = <F extends string, E extends HTMLElement, R>(props: UseElementBindingProps<F, E, R>) => {
  useEffect(() => {
    const element = props.element.current as (E & { [K in F]: R }) | null;
    if (_notNil(element)) {
      Reflect.defineProperty(element, props.field, {
        configurable: true,
        enumerable: false,
        get: () => props.accessor(),
      });
      return () => {
        Reflect.deleteProperty(element, props.field);
      };
    }
  }, [props.element, props.field]);
};

export default useElementBinding;
